import parse from 'utils/parse';
import application from 'services/application/application';
import { VersionMediaKey } from 'services/api/proxy-performer-profile-picture/data-contracts';
import type { AchievementWinnerProfilePicture } from 'services/api/proxy-bff-web/data-contracts';
import { type MessageDescriptor } from 'react-intl';
import { link } from 'services/navigation';

import topExpertsId from '../top-experts/@@global/id';
import topExpertsLastMonthWinnersId from '../top-experts-last-month-winners/@@global/id';
import topModelsSummerContestId from '../top-models-summer-contest/@@global/id';
import topModelsSummerContestWinnersId from '../top-models-summer-contest-winners/@@global/id';

import topModelsId from './@@global/id';
import messages from './top-models.i18n';
import { type TabConfig } from './contest-tabs/contracts';
import { type PeriodOption } from './contracts';

const makeTestId = (...ids: string[]): string =>
  parse.toTestId((application.Oranum && 'top-experts') || 'top-models', ...ids);

const testId = Object.freeze({
  page: makeTestId(),
  moreInfoLink: makeTestId('more-info-link'),
  moreInfoButton: makeTestId('more-info-button'),
  moreInfoModal: makeTestId('more-info-modal'),
  periodFilter: makeTestId('period-filter'),
  periodFilterOptionMonth: makeTestId('period-filter-option-month'),
  periodFilterOptionWeek: makeTestId('period-filter-option-week'),
  periodFilterOptionDay: makeTestId('period-filter-option-day'),
  pageTabs: makeTestId('page-tabs'),
  pageTabSummerContest: makeTestId('page-tabs-summer-contest'),
  pageTabSummerContestWinners: makeTestId('page-tabs-summer-contest-winners'),
  pageTabTop100: makeTestId('page-tab-top-100'),
  pageTabCurrent: makeTestId('page-tab-current'),
  pageTabLastMonth: makeTestId('page-tab-last-month'),
  header: makeTestId('header'),
  subHeader: makeTestId('sub-header'),
  errorMessage: makeTestId('error-message'),
  summerContestEmptyState: makeTestId('summer-contest-empty-state'),
  leaderList: makeTestId('leader-list'),
  leaderListItem: (itemRank: number) => makeTestId(`leader-list-item-${itemRank}`),
  leaderListItemRank: (itemRank: number) => makeTestId(`leader-list-item-rank-${itemRank}`),
  leaderListItemName: (itemRank: number) => makeTestId(`leader-list-item-name-${itemRank}`),
  leaderListItemAvatar: (itemRank: number) => makeTestId(`leader-list-item-avatar-${itemRank}`),
  leaderListItemReward: (itemRank: number) => makeTestId(`leader-list-item-reward-${itemRank}`),
  leaderListItemPoints: (itemRank: number) => makeTestId(`leader-list-item-points-${itemRank}`),
});

const periodOptions: { id: PeriodOption; testId: string; label: MessageDescriptor }[] = [
  {
    id: 'month',
    testId: testId.periodFilterOptionMonth,
    label: messages.pages.bestOf.topModels.periodFilter.monthly,
  },
  {
    id: 'week',
    testId: testId.periodFilterOptionWeek,
    label: messages.pages.bestOf.topModels.periodFilter.weekly,
  },
  {
    id: 'day',
    testId: testId.periodFilterOptionDay,
    label: messages.pages.bestOf.topModels.periodFilter.daily,
  },
];

const summerContestStartDate = '2025-07-28T00:00:00+02:00';
const summerContestEndDate = '2025-09-01T23:59:00+02:00';

const defaultPeriodOption: PeriodOption = 'month';

const winnersAvatarMediaKey: AchievementWinnerProfilePicture['mediaKey'] = 'profile_picture_crop_4_3_460_345_jpg';

/** Uses the ongoing contest achievement API and top-models.store */
const ongoingListTypes = ['current', 'summer-contest'];
/** Uses the achievement winners API and top-models-winners.store */
const winnersListTypes = ['summer-contest-winners', 'last-month'];

const config = {
  skeletonLeaderCount: 10,
  numTopModels: 100,
  numSummerContestTopModels: 200,
  monthlyGiveawayAmount: 5000,
  summerContestPrize: 250_000,
  periodOptions,
  defaultPeriodOption,
  leaderAvatarMediaKey: VersionMediaKey.ProfilePictureCrop43147110Jpg,
  winnersAvatarMediaKey,
  summerContestStartDate,
  summerContestEndDate,
};

const contestTabs: TabConfig[] = [
  {
    id: topExpertsId,
    testId: testId.pageTabCurrent,
    location: link.topExperts(),
    label: {
      id: messages.pages.bestOf.topModels.pageTabs.current,
    },
  },
  {
    id: topExpertsLastMonthWinnersId,
    testId: testId.pageTabLastMonth,
    location: link.topExpertsLastMonthWinners(),
    label: {
      id: messages.pages.bestOf.topModels.pageTabs.lastMonth,
    },
  },
];

const summerContestTabs: TabConfig[] = [
  {
    id: topModelsSummerContestId,
    testId: testId.pageTabSummerContest,
    location: link.topModelsSummerContest(),
    label: {
      id: messages.pages.bestOf.topModels.pageTabs.summerContest,
    },
  },
  {
    id: topModelsId,
    testId: testId.pageTabTop100,
    location: link.topModels({ forceShow: '1' }),
    label: {
      id: messages.pages.bestOf.topModels.pageTabs.top,
      values: { numTopModels: config.numTopModels },
    },
  },
];

const summerContestWinnersTabs: TabConfig[] = [
  summerContestTabs[1],
  {
    id: topModelsSummerContestWinnersId,
    testId: testId.pageTabSummerContestWinners,
    location: link.topModelsSummerContestWinners(),
    label: {
      id: messages.pages.bestOf.topModels.pageTabs.summerContestWinners,
    },
  },
];

export { testId, config, summerContestTabs, contestTabs, summerContestWinnersTabs, ongoingListTypes, winnersListTypes };
